import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  saveClinicalAssessment,
  getClinicalAssessment,
  addVisit,
  downloadClinicalAssessmentDocument,
  viewClinicalAssessmentDocument,
  deleteClinicalAssessmentDocument,
} from "../../../api/apiService";
import { getPatientById } from "../../../Services/patientService";
import { formatPatientName } from "../../../utils/patientVisitUtils";
import { sanitizeClinicalNote, sanitizeText } from "../../../utils/inputValidation";
import {
  validateDocumentBatch,
  validateDocumentFile,
} from "../../../utils/fileSecurityValidation";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";
import Trash from "../../../assets/images/comman/trash-outline.svg";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";

const Assessment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const { can, user } = useAuth();
  const canWriteAssessment = can(PERMISSIONS.CLINICAL_ASSESSMENT_WRITE);

  const [patientData, setPatientData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [deletingDocIndex, setDeletingDocIndex] = useState(null);
  const [downloadingDocIndex, setDownloadingDocIndex] = useState(null);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadPatient = async () => {
      let data = location.state?.patientData;
      const queryParams = new URLSearchParams(location.search);
      const patientIdFromQuery =
        queryParams.get("patientId") || location.state?.patientId;
      const targetId = data?._id || data?.id || patientIdFromQuery;

      if (targetId) {
        try {
          const fetched = await getPatientById(targetId);
          if (fetched && isMounted) {
            data = {
              ...(data || {}),
              ...fetched,
              _id: fetched._id || fetched.id || targetId,
              id: fetched._id || fetched.id || targetId,
            };
          }
        } catch (e) {
          console.warn("Could not load patient for assessment:", e);
        }
      }

      if (isMounted && data) {
        const rawName =
          (data.name && typeof data.name === "string" && data.name.trim() && data.name.trim() !== "undefined undefined")
            ? data.name.trim()
            : (data.fullName && typeof data.fullName === "string" && data.fullName.trim() && data.fullName.trim() !== "undefined undefined")
            ? data.fullName.trim()
            : formatPatientName(data) || "Patient";

        let resolvedVisitId =
          data.visitId ||
          data.visits?.[0]?.visitId ||
          data.visits?.[0]?._id ||
          data.visits?.[0]?.id ||
          location.state?.patientData?.visitId ||
          location.state?.patientData?.visits?.[0]?.visitId ||
          location.state?.patientData?.visits?.[0]?._id ||
          "";

        // If patient has no visit in record, auto-create an OPD visit so assessment can be saved
        if (!resolvedVisitId && targetId) {
          try {
            const newVisitRes = await addVisit(targetId, {
              visitType: data.visitType || "General OPD",
              department: data.department || "",
              doctor: data.doctor || "",
            });
            const createdVisit =
              newVisitRes?.data?.visits?.[0] ||
              newVisitRes?.visit ||
              newVisitRes?.data;
            resolvedVisitId =
              newVisitRes?.visitId ||
              createdVisit?.visitId ||
              createdVisit?._id ||
              createdVisit?.id ||
              "";
          } catch (vErr) {
            console.warn("Could not auto-ensure visit in assessment:", vErr);
          }
        }

        setPatientData({
          ...data,
          visitId: resolvedVisitId,
          name: rawName,
          fullName: rawName,
          calculatedAge: data.calculatedAge || data.age || "",
        });
      }
    };
    loadPatient();
    return () => {
      isMounted = false;
    };
  }, [location.state, location.search]);

  // Form state for all assessment fields
  const [assessmentData, setAssessmentData] = useState({
    // Vitals
    pulse: "",
    spo2: "",
    sbp: "",
    dbp: "",
    map: "",
    temperature: "",
    respiration: "",
    painScore: "",
    height: "",
    weight: "",
    bsa: "",
    bmi: "",
    category: "",
    bs: "",
    creatinine: "",
    egfr: "",
    egfr2: "",

    // Symptoms
    symptoms: "",
    selectedSymptoms: "",

    // Consent
    dataConsent: false,
  });

  // Immunization rich-object state
  const [immunizationData, setImmunizationData] = useState({
    covid19Dose1: { date: "", manufacturer: "", lotNumber: "", doseNumber: 1 },
    covid19Dose2: { date: "", manufacturer: "", lotNumber: "", doseNumber: 2 },
    tetanusBooster: { date: "", manufacturer: "", lotNumber: "", doseNumber: 1 },
    fluVaccine: { date: "", manufacturer: "", lotNumber: "", doseNumber: 1 },
  });

  const handleImmunizationChange = (vaccineKey, field, value) => {
    setImmunizationData((prev) => ({
      ...prev,
      [vaccineKey]: {
        ...prev[vaccineKey],
        [field]: value,
      },
    }));
  };

  // Get today's date in YYYY-MM-DD format for max date restriction
  const getTodaysDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setAssessmentData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const { validFiles, errors } = validateDocumentBatch(files, selectedFiles);

    if (errors.length > 0) {
      errors.forEach((err) => showError(err));
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      if (validFiles.length === 1) {
        showSuccess(`File "${validFiles[0].name}" staged for upload.`);
      } else {
        showSuccess(`${validFiles.length} files staged for upload.`);
      }
    }

    // Reset the input so the same file can be selected again if removed
    e.target.value = null;
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatUploadDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime())
        ? dateStr
        : d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
    } catch {
      return dateStr;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes == null || bytes === "" || isNaN(bytes)) return "";
    const num = Number(bytes);
    if (num <= 0) return "";
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    if (num < 1024 * 1024 * 1024) return `${(num / (1024 * 1024)).toFixed(1)} MB`;
    return `${(num / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const getOriginalFileName = (doc, fallback = "") => {
    if (!doc) return fallback;
    if (typeof doc === "string") {
      return doc
        .replace(/^(?:files-)?\d{10,14}[-_]/, "")
        .replace(/^[a-f0-9]{24,32}[-_]/, "");
    }
    const rawName =
      doc.originalname ||
      doc.originalName ||
      doc.name ||
      doc.fileName ||
      doc.filename ||
      doc.title ||
      "";
    if (!rawName || typeof rawName !== "string") return fallback;
    return rawName
      .replace(/^(?:files-)?\d{10,14}[-_]/, "")
      .replace(/^[a-f0-9]{24,32}[-_]/, "");
  };

  const handleViewDocument = async (docIndex, fileName) => {
    const visitId =
      patientData.visitId ||
      patientData.visits?.[0]?.visitId ||
      patientData.visits?.[0]?._id ||
      patientData.visits?.[0]?.id ||
      location.state?.patientData?.visitId ||
      location.state?.patientData?.visits?.[0]?.visitId ||
      "";
    const patientIdForQuery =
      location.state?.patientId || patientData._id || patientData.id || "";

    if (!visitId) {
      showError("Visit ID is missing. Cannot view document.");
      return;
    }

    try {
      setDownloadingDocIndex(docIndex);
      await viewClinicalAssessmentDocument(
        visitId,
        docIndex,
        patientIdForQuery || undefined
      );
    } catch (err) {
      console.error("Error viewing document:", err);
      showError(
        err.response?.data?.message || err.message || "Failed to view document."
      );
    } finally {
      setDownloadingDocIndex(null);
    }
  };

  const handleDownloadDocument = async (docIndex, fileName) => {
    const visitId =
      patientData.visitId ||
      patientData.visits?.[0]?.visitId ||
      patientData.visits?.[0]?._id ||
      patientData.visits?.[0]?.id ||
      location.state?.patientData?.visitId ||
      location.state?.patientData?.visits?.[0]?.visitId ||
      "";
    const patientIdForQuery =
      location.state?.patientId || patientData._id || patientData.id || "";

    if (!visitId) {
      showError("Visit ID is missing. Cannot download document.");
      return;
    }

    const doc = existingDocuments[docIndex];
    const resolvedName = fileName || getOriginalFileName(doc);

    try {
      setDownloadingDocIndex(docIndex);
      await downloadClinicalAssessmentDocument(
        visitId,
        docIndex,
        patientIdForQuery || undefined,
        resolvedName
      );
    } catch (err) {
      console.error("Error downloading document:", err);
      showError(
        err.response?.data?.message ||
          err.message ||
          "Failed to download document."
      );
    } finally {
      setDownloadingDocIndex(null);
    }
  };

  const handleDeleteDocument = async (docIndex, fileName) => {
    const visitId =
      patientData.visitId ||
      patientData.visits?.[0]?.visitId ||
      patientData.visits?.[0]?._id ||
      patientData.visits?.[0]?.id ||
      location.state?.patientData?.visitId ||
      location.state?.patientData?.visits?.[0]?.visitId ||
      "";
    const patientIdForQuery =
      location.state?.patientId || patientData._id || patientData.id || "";

    if (!visitId) {
      showError("Visit ID is missing. Cannot delete document.");
      return;
    }

    try {
      setDeletingDocIndex(docIndex);
      const res = await deleteClinicalAssessmentDocument(
        visitId,
        docIndex,
        patientIdForQuery || undefined
      );
      showSuccess(res?.message || "Document deleted successfully.");
      setConfirmDeleteIndex(null);
      setExistingDocuments((prev) => prev.filter((_, idx) => idx !== docIndex));
      try {
        const refreshRes = await getClinicalAssessment(
          visitId,
          patientIdForQuery || undefined
        );
        const refPayload =
          refreshRes?.data || refreshRes?.assessment || refreshRes;
        if (Array.isArray(refPayload?.documentUploads)) {
          setExistingDocuments(refPayload.documentUploads);
        }
      } catch {}
    } catch (err) {
      console.error("Error deleting document:", err);
      showError(
        err.response?.data?.message ||
          err.message ||
          "Failed to delete document."
      );
    } finally {
      setDeletingDocIndex(null);
    }
  };

  const createNewItem = () => ({
    id: Date.now(),
  });

  // Load existing clinical assessment (if any) and pre-fill the form
  useEffect(() => {
    const loadExistingAssessment = async () => {
      try {
        // Resolve visitId for this assessment page
        const visitId =
          patientData.visitId ||
          patientData.visits?.[0]?.visitId ||
          patientData.visits?.[0]?._id ||
          patientData.visits?.[0]?.id ||
          location.state?.patientData?.visitId ||
          location.state?.patientData?.visits?.[0]?.visitId ||
          location.state?.patientData?.visits?.[0]?._id ||
          "";

        // Resolve patientId for query
        const patientIdForQuery =
          location.state?.patientId || patientData._id || patientData.id || "";

        if (!visitId || !patientIdForQuery) {
          return;
        }

        const response = await getClinicalAssessment(
          visitId,
          patientIdForQuery,
        );

        // The backend may wrap the payload; try common shapes
        const payload =
          response?.data || response?.assessment || response || undefined;

        if (!payload) {
          return;
        }

        const vitals = payload.vitals || {};
        const immunization = payload.immunization || {};

        if (Array.isArray(payload.documentUploads)) {
          setExistingDocuments(payload.documentUploads);
        }

        // Basic fields (vitals, symptoms)
        setAssessmentData((prev) => ({
          ...prev,
          pulse: vitals.pulse ?? prev.pulse,
          spo2: vitals.spo2 ?? prev.spo2,
          sbp: vitals.sbp ?? prev.sbp,
          dbp: vitals.dbp ?? prev.dbp,
          temperature: vitals.temp ?? vitals.temperature ?? prev.temperature,
          height: vitals.height ?? prev.height,
          weight: vitals.weight ?? prev.weight,
          symptoms: payload.symptomsComplaints ?? prev.symptoms,
          dataConsent:
            payload.dataConsent !== undefined
              ? Boolean(payload.dataConsent)
              : payload.consent !== undefined
              ? Boolean(payload.consent)
              : prev.dataConsent,
        }));

        // Prefill immunization — support both rich-object (new) and flat-date (legacy) format
        setImmunizationData((prev) => {
          const mapVaccine = (richKey, legacyDateKey) => {
            const rich = immunization[richKey];
            if (rich && typeof rich === "object") {
              return {
                date: rich.date ?? prev[richKey].date,
                manufacturer: rich.manufacturer ?? prev[richKey].manufacturer,
                lotNumber: rich.lotNumber ?? prev[richKey].lotNumber,
                doseNumber: rich.doseNumber ?? prev[richKey].doseNumber,
              };
            }
            // Legacy flat date
            const legacyDate = immunization[legacyDateKey];
            if (legacyDate) {
              return { ...prev[richKey], date: legacyDate };
            }
            return prev[richKey];
          };
          return {
            covid19Dose1: mapVaccine("covid19Dose1", "covid19Dose1Date"),
            covid19Dose2: mapVaccine("covid19Dose2", "covid19Dose2Date"),
            tetanusBooster: mapVaccine("tetanusBooster", "tetanusBoosterDate"),
            fluVaccine: mapVaccine("fluVaccine", "fluVaccineDate"),
          };
        });

        // Medical History
        const serverMedicalHistory = payload.medicalHistory || [];
        if (
          Array.isArray(serverMedicalHistory) &&
          serverMedicalHistory.length
        ) {
          const items = serverMedicalHistory.map(() => createNewItem());
          const dataMap = {};
          items.forEach((item, index) => {
            const src = serverMedicalHistory[index] || {};
            dataMap[item.id] = {
              disease: src.disease || "",
              duration: src.duration || "",
              medication: src.medications || src.medication || "",
            };
          });
          setMedicalHisToryitems(items);
          setMedicalHistoryData(dataMap);
        }

        // Surgical History
        const serverSurgicalHistory = payload.surgicalHistory || [];
        if (
          Array.isArray(serverSurgicalHistory) &&
          serverSurgicalHistory.length
        ) {
          const items = serverSurgicalHistory.map(() => createNewItem());
          const dataMap = {};
          items.forEach((item, index) => {
            const src = serverSurgicalHistory[index] || {};
            dataMap[item.id] = {
              surgical: src.surgical || "",
              surgeonName: src.surgeonName || "",
              date: src.date || "",
              hospital: src.hospital || "",
            };
          });
          setMedicalSurgicalitems(items);
          setSurgicalHistoryData(dataMap);
        }

        // Additional Details
        const serverAdditionalDetails = payload.additionalDetails || [];
        if (
          Array.isArray(serverAdditionalDetails) &&
          serverAdditionalDetails.length
        ) {
          const items = serverAdditionalDetails.map(() => createNewItem());
          const dataMap = {};
          items.forEach((item, index) => {
            const src = serverAdditionalDetails[index] || {};
            dataMap[item.id] = {
              type: src.type || "",
              duration: src.duration || "",
              units: src.units || "",
              frequency: src.frequency || "",
              action: src.action || "",
            };
          });
          setAdditionalDetails(items);
          setAdditionalDetailsData(dataMap);
        }

        // Personal History
        const serverPersonalHistory = payload.personalHistory || [];
        if (
          Array.isArray(serverPersonalHistory) &&
          serverPersonalHistory.length
        ) {
          const items = serverPersonalHistory.map(() => createNewItem());
          const dataMap = {};
          items.forEach((item, index) => {
            const src = serverPersonalHistory[index] || {};
            dataMap[item.id] = {
              diet: src.diet || "",
              appetite: src.appetite || "",
              sleep: src.sleep || "",
              bladder: src.bladder || src.blader || "",
              bowel: src.bowel || "",
            };
          });
          setAddPersonalHistory(items);
          setPersonalHistoryData(dataMap);
        }
        // Physical Activity
        if (payload.physicalActivity) {
          setPhysicalActivity({
            stepsPerDay: payload.physicalActivity.stepsPerDay ?? "",
            caloriesBurned: payload.physicalActivity.caloriesBurned ?? "",
            sleepDuration: payload.physicalActivity.sleepDuration ?? "",
          });
        }

        // Lifestyle
        if (payload.lifestyle) {
          setLifestyle({
            dietType: payload.lifestyle.dietType ?? "",
            smokingBehavior: payload.lifestyle.smokingBehavior ?? "",
            alcoholBehavior: payload.lifestyle.alcoholBehavior ?? "",
          });
        }

        // Women Health
        if (payload.womenHealth) {
          setWomenHealth({
            ageAtMenarche: payload.womenHealth.ageAtMenarche ?? "",
            lastMenstrualPeriod: payload.womenHealth.lastMenstrualPeriod ?? "",
          });
        }
      } catch (error) {
        // Fail silently in UI, just log for debugging
        console.error("Failed to load existing clinical assessment:", error);
      }
    };

    loadExistingAssessment();
  }, [location.state, patientData]);

  const [medicalHisToryitems, setMedicalHisToryitems] = useState([
    createNewItem(),
  ]);
  const [medicalHistoryData, setMedicalHistoryData] = useState({});

  const handleMedicalHistory = () => {
    setMedicalHisToryitems((prev) => [...prev, createNewItem()]);
  };
  const handleRemoveMedicalHistory = (id) => {
    if (medicalHisToryitems.length > 1) {
      setMedicalHisToryitems((prev) => prev.filter((item) => item.id !== id));
      // Remove data for this item
      setMedicalHistoryData((prev) => {
        const newData = { ...prev };
        delete newData[id];
        return newData;
      });
    }
  };

  const handleMedicalHistoryChange = (id, field, value) => {
    setMedicalHistoryData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };
  // End Medical History

  const [medicalSurgicalitems, setMedicalSurgicalitems] = useState([
    createNewItem(),
  ]);
  const [surgicalHistoryData, setSurgicalHistoryData] = useState({});

  const handleSurgicalHistory = () => {
    setMedicalSurgicalitems((prev) => [...prev, createNewItem()]);
  };
  const handleRemoveSurgicalHistory = (id) => {
    if (medicalSurgicalitems.length > 1) {
      setMedicalSurgicalitems((prev) => prev.filter((item) => item.id !== id));
      setSurgicalHistoryData((prev) => {
        const newData = { ...prev };
        delete newData[id];
        return newData;
      });
    }
  };

  const handleSurgicalHistoryChange = (id, field, value) => {
    setSurgicalHistoryData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };
  // End Surgical History

  const [additionalDetails, setAdditionalDetails] = useState([createNewItem()]);
  const [additionalDetailsData, setAdditionalDetailsData] = useState({});

  const handleAdditionalDetails = () => {
    setAdditionalDetails((prev) => [...prev, createNewItem()]);
  };
  const handleRemoveAdditionalDetail = (id) => {
    if (additionalDetails.length > 1) {
      setAdditionalDetails((prev) => prev.filter((item) => item.id !== id));
      setAdditionalDetailsData((prev) => {
        const newData = { ...prev };
        delete newData[id];
        return newData;
      });
    }
  };

  const handleAdditionalDetailsChange = (id, field, value) => {
    setAdditionalDetailsData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };
  // End Additional Details

  const [addPersonalHistory, setAddPersonalHistory] = useState([
    createNewItem(),
  ]);
  const [personalHistoryData, setPersonalHistoryData] = useState({});

  const handlePersonalHistory = () => {
    setAddPersonalHistory((prev) => [...prev, createNewItem()]);
  };
  const handleRemovehandlePersonalHistory = (id) => {
    if (addPersonalHistory.length > 1) {
      setAddPersonalHistory((prev) => prev.filter((item) => item.id !== id));
      setPersonalHistoryData((prev) => {
        const newData = { ...prev };
        delete newData[id];
        return newData;
      });
    }
  };

  const handlePersonalHistoryChange = (id, field, value) => {
    setPersonalHistoryData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  // ── New structured section state ──────────────────────────────────────────
  const [physicalActivity, setPhysicalActivity] = useState({
    stepsPerDay: "",
    caloriesBurned: "",
    sleepDuration: "",
  });

  const [lifestyle, setLifestyle] = useState({
    dietType: "",
    smokingBehavior: "",
    alcoholBehavior: "",
  });

  const [womenHealth, setWomenHealth] = useState({
    ageAtMenarche: "",
    lastMenstrualPeriod: "",
  });
  // ─────────────────────────────────────────────────────────────────────────

  // Save function to call vitals API first, then registration API
  const handleSave = async () => {
    if (!canWriteAssessment) {
      showError("You do not have permission to save clinical assessments.");
      return;
    }
    setIsSaving(true);

    try {
      // Check if we have valid patient data
      //   if (!patientData.ABHANumber && !patientData.abhaNumber) {
      //     showError("Patient ABHA Number is missing. Cannot save assessment.");
      //     setIsSaving(false);
      //     return;
      //   }

      // Extract ABHA Number and remove dashes/hyphens
      const getCleanAbhaNumber = () => {
        const abhaNumber =
          patientData.ABHANumber ||
          patientData.abhaNumber ||
          patientData.abha_number ||
          patientData.formattedAbhaId ||
          "";
        // Remove all dashes, hyphens, and spaces
        return abhaNumber.replace(/[-\s]/g, "");
      };

      // Extract ABHA Address - check multiple possible fields
      const getAbhaAddress = () => {
        return (
          patientData.ABHAAddress ||
          patientData.abhaAddress ||
          patientData.abhaaddress || // Added lowercase version as stored in DB
          patientData.abha_address ||
          `${getCleanAbhaNumber()}@sbx`
        ); // Default format
      };

      // Extract Year of Birth - check multiple possible fields
      const getYearOfBirth = () => {
        // If yearOfBirth exists, use it
        if (patientData.yearOfBirth) {
          return parseInt(patientData.yearOfBirth);
        }

        // Extract from date of birth
        if (patientData.dob) {
          const year = new Date(patientData.dob).getFullYear();
          return isNaN(year) ? null : year;
        }

        // Extract from dateOfBirth field
        if (patientData.dateOfBirth) {
          const year = new Date(patientData.dateOfBirth).getFullYear();
          return isNaN(year) ? null : year;
        }

        // If age is available, calculate year of birth
        if (patientData.age) {
          const currentYear = new Date().getFullYear();
          const ageNum = parseInt(patientData.age);
          return isNaN(ageNum) ? null : currentYear - ageNum;
        }

        return null;
      };


      // Validate required fields
      //   if (!cleanAbhaNumber) {
      //     showError("Patient ABHA Number is missing. Cannot save assessment.");
      //     setIsSaving(false);
      //     return;
      //   }

      //   if (!abhaAddress) {
      //     showError("Patient ABHA Address is missing. Cannot save assessment.");
      //     setIsSaving(false);
      //     return;
      //   }

      //   if (!yearOfBirth) {
      //     showError("Patient Year of Birth is missing. Cannot save assessment.");
      //     setIsSaving(false);
      //     return;
      //   }

      // Resolve visitId (required for clinical assessment API)
      let visitId =
        patientData.visitId ||
        patientData.visits?.[0]?.visitId ||
        patientData.visits?.[0]?._id ||
        patientData.visits?.[0]?.id ||
        location.state?.patientData?.visitId ||
        location.state?.patientData?.visits?.[0]?.visitId ||
        location.state?.patientData?.visits?.[0]?._id ||
        "";

      // If visitId is still missing, attempt to create one on-the-fly before failing
      if (!visitId && (patientData._id || patientData.id)) {
        try {
          const fallbackRes = await addVisit(patientData._id || patientData.id, {
            visitType: patientData.visitType || "General OPD",
            department: patientData.department || "",
            doctor: patientData.doctor || "",
          });
          const createdV = fallbackRes?.data?.visits?.[0] || fallbackRes?.visit || fallbackRes?.data;
          visitId = fallbackRes?.visitId || createdV?.visitId || createdV?._id || createdV?.id || "";
          if (visitId) {
            setPatientData((prev) => ({ ...prev, visitId }));
          }
        } catch (vErr) {
          console.warn("Could not create fallback visit on save:", vErr);
        }
      }

      if (!visitId) {
        showError("Visit ID is missing. Cannot save assessment.");
        setIsSaving(false);
        return;
      }

      // Resolve patientId for manual visits (optional query param)
      const patientIdForQuery =
        location.state?.patientId || patientData._id || patientData.id || "";

      // Build assessment payload for POST /visit/:visitId/clinical/assessment
      const vitalsForAssessment = {};
      const mapVitalField = (sourceKey, targetKey = sourceKey) => {
        const val = assessmentData[sourceKey];
        if (val !== undefined && val !== null && val !== "") {
          const numericFields = [
            "pulse",
            "spo2",
            "sbp",
            "dbp",
            "height",
            "weight",
          ];
          if (numericFields.includes(sourceKey) && !isNaN(Number(val))) {
            vitalsForAssessment[targetKey] = Number(val);
          } else {
            vitalsForAssessment[targetKey] = val;
          }
        }
      };

      mapVitalField("pulse");
      mapVitalField("spo2");
      mapVitalField("sbp");
      mapVitalField("dbp");
      mapVitalField("temperature", "temp");
      mapVitalField("height");
      mapVitalField("weight");

      const immunizationForAssessment = {};
      const vaccineKeys = [
        ["covid19Dose1", "covid19Dose1"],
        ["covid19Dose2", "covid19Dose2"],
        ["tetanusBooster", "tetanusBooster"],
        ["fluVaccine", "fluVaccine"],
      ];
      vaccineKeys.forEach(([stateKey, payloadKey]) => {
        const entry = immunizationData[stateKey];
        if (entry?.date) {
          immunizationForAssessment[payloadKey] = {
            date: entry.date,
            ...(entry.manufacturer && entry.manufacturer.trim() && { manufacturer: entry.manufacturer.trim() }),
            ...(entry.lotNumber && entry.lotNumber.trim() && { lotNumber: entry.lotNumber.trim() }),
            doseNumber: parseInt(entry.doseNumber, 10) || 1,
          };
        }
      });

      const buildHistoryArray = (dataObj, fieldMap = {}) =>
        Object.values(dataObj || {})
          .map((item) => {
            const mapped = {};
            Object.entries(item || {}).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                const targetKey = fieldMap[key] || key;
                mapped[targetKey] = typeof value === "string" ? sanitizeText(value) : value;
              }
            });
            return mapped;
          })
          .filter((item) => Object.keys(item).length > 0);

      const medicalHistoryPayload = buildHistoryArray(medicalHistoryData, {
        medication: "medications",
      });
      const surgicalHistoryPayload = buildHistoryArray(surgicalHistoryData);
      const additionalDetailsPayload = buildHistoryArray(additionalDetailsData);
      const personalHistoryPayload = buildHistoryArray(personalHistoryData);

      const formData = new FormData();

      if (Object.keys(vitalsForAssessment).length > 0) {
        formData.append("vitals", JSON.stringify(vitalsForAssessment));
      }
      if (Object.keys(immunizationForAssessment).length > 0) {
        formData.append(
          "immunization",
          JSON.stringify(immunizationForAssessment),
        );
      }
      if (assessmentData.symptoms && assessmentData.symptoms !== "") {
        formData.append("symptomsComplaints", sanitizeClinicalNote(assessmentData.symptoms));
      }
      if (medicalHistoryPayload.length > 0) {
        formData.append(
          "medicalHistory",
          JSON.stringify(medicalHistoryPayload),
        );
      }
      if (surgicalHistoryPayload.length > 0) {
        formData.append(
          "surgicalHistory",
          JSON.stringify(surgicalHistoryPayload),
        );
      }
      if (additionalDetailsPayload.length > 0) {
        formData.append(
          "additionalDetails",
          JSON.stringify(additionalDetailsPayload),
        );
      }
      if (personalHistoryPayload.length > 0) {
        formData.append(
          "personalHistory",
          JSON.stringify(personalHistoryPayload),
        );
      }

      // Physical Activity
      const _pa = {};
      if (physicalActivity.stepsPerDay !== "") _pa.stepsPerDay = Number(physicalActivity.stepsPerDay);
      if (physicalActivity.caloriesBurned !== "") _pa.caloriesBurned = Number(physicalActivity.caloriesBurned);
      if (physicalActivity.sleepDuration !== "") _pa.sleepDuration = Number(physicalActivity.sleepDuration);
      if (Object.keys(_pa).length > 0) {
        formData.append("physicalActivity", JSON.stringify(_pa));
      }

      // Lifestyle
      const _ls = {};
      if (lifestyle.dietType) _ls.dietType = lifestyle.dietType;
      if (lifestyle.smokingBehavior) _ls.smokingBehavior = lifestyle.smokingBehavior;
      if (lifestyle.alcoholBehavior) _ls.alcoholBehavior = lifestyle.alcoholBehavior;
      if (Object.keys(_ls).length > 0) {
        formData.append("lifestyle", JSON.stringify(_ls));
      }

      // Women Health — only for female patients
      const _isFemale = ["female", "f"].includes(
        (patientData.gender || "").toLowerCase().trim(),
      );
      if (_isFemale) {
        const _wh = {};
        if (womenHealth.ageAtMenarche !== "") _wh.ageAtMenarche = Number(womenHealth.ageAtMenarche);
        if (womenHealth.lastMenstrualPeriod) _wh.lastMenstrualPeriod = womenHealth.lastMenstrualPeriod;
        if (Object.keys(_wh).length > 0) {
          formData.append("womenHealth", JSON.stringify(_wh));
        }
      }

      // ABDM Data Sharing Consent
      formData.append("dataConsent", assessmentData.dataConsent ? "true" : "false");
      formData.append("consent", assessmentData.dataConsent ? "true" : "false");

      // Pre-upload security validation: verify all staged files strictly comply with policy
      for (const file of selectedFiles) {
        const check = validateDocumentFile(file);
        if (!check.valid) {
          showError(`Cannot save assessment: ${check.error}`);
          setIsSaving(false);
          return;
        }
      }

      // Append files with exact original uploaded name preserved
      selectedFiles.forEach((file) => {
        formData.append("files", file, file.name);
      });

      // Save OPD clinical assessment (replaces old saveVitals; uses apiService base URL)
      let assessmentResponse;
      try {
        assessmentResponse = await saveClinicalAssessment(
          visitId,
          formData,
          patientIdForQuery || undefined,
        );
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          err.message ||
          "Failed to save assessment.";
        const errors = err.response?.data?.errors;
        showError(errors?.length ? `${msg} ${errors.join(" ")}` : msg);
        setIsSaving(false);
        return;
      }

      if (assessmentResponse?.status === "success") {
        showSuccess(
          assessmentResponse.message || "Assessment saved successfully.",
        );
        setSelectedFiles([]);
        if (Array.isArray(assessmentResponse?.data?.documentUploads)) {
          setExistingDocuments(assessmentResponse.data.documentUploads);
        }
      } else {
        showError(assessmentResponse?.message || "Failed to save assessment.");
        setIsSaving(false);
        return;
      }

      setTimeout(() => {
        navigate("/OPDDetails");
      }, 2000);
    } catch (error) {
      console.error("Save assessment error:", error);
      showError("Failed to save assessment data: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="container-fluid">
        <div className="assessment-page">
          {/* Patient Information Header */}
          <div className="basicDetails mb-3">
            <h4>Patient Information</h4>
            <div className="patientsFeildsRow">
              <div className="row">
                <div className="col-md-3">
                  <strong>Name:</strong>{" "}
                  {patientData.fullName ||
                    patientData.name ||
                    (
                      (patientData.f_name || "") +
                      " " +
                      (patientData.m_name || "") +
                      " " +
                      (patientData.l_name || "")
                    ).trim() ||
                    "N/A"}
                </div>
                <div className="col-md-3">
                  <strong>ABHA Number:</strong>{" "}
                  {patientData.ABHANumber || patientData.abhaNumber || "N/A"}
                </div>
                <div className="col-md-3">
                  <strong>ABHA Address:</strong>{" "}
                  {patientData.ABHAAddress ||
                    patientData.abhaAddress ||
                    patientData.abhaaddress ||
                    patientData.email ||
                    "N/A"}
                </div>
                <div className="col-md-3">
                  <strong>Gender:</strong> {patientData.gender || "N/A"}
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md-3">
                  <strong>Date of Birth:</strong>{" "}
                  {patientData.dob && !isNaN(new Date(patientData.dob).getTime())
                    ? new Date(patientData.dob).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "N/A"}
                </div>
                <div className="col-md-3">
                  <strong>Year of Birth:</strong>{" "}
                  {patientData.yearOfBirth ||
                    (patientData.dob && !isNaN(new Date(patientData.dob).getTime())
                      ? new Date(patientData.dob).getFullYear()
                      : "N/A")}
                </div>
                <div className="col-md-3">
                  <strong>Age:</strong>{" "}
                  {patientData.calculatedAge || patientData.age || "N/A"}
                </div>
                <div className="col-md-3">
                  <strong>Mobile:</strong> {patientData.mobile || "N/A"}
                </div>
              </div>
            </div>
          </div>

          {!canWriteAssessment && (
            <>
              <style>{`
                .allMainFeilds input, .allMainFeilds select, .allMainFeilds textarea {
                  background-color: #f8f9fa !important;
                  cursor: not-allowed !important;
                  color: #495057 !important;
                }
              `}</style>
              <div
                className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2"
                role="alert"
                style={{
                  borderRadius: "8px",
                  fontSize: "13px",
                  backgroundColor: "#EBF5FF",
                  color: "#1E429F",
                  border: "1px solid #B4C6FC",
                }}
              >
                <i className="material-icons" style={{ fontSize: "18px" }}>visibility</i>
                <span><strong>Read-Only Mode:</strong> You do not have clinical assessment write permissions. All fields are locked for viewing only.</span>
              </div>
            </>
          )}

          <div className="allMainFeilds">
            <fieldset
              disabled={!canWriteAssessment}
              style={{
                border: "none",
                padding: 0,
                margin: 0,
                pointerEvents: !canWriteAssessment ? "none" : "auto",
                opacity: !canWriteAssessment ? 0.88 : 1,
              }}
            >
            {/* <form onSubmit={handleSave}> */}
            <div className="basicDetails">
              <h4>Vitals Details</h4>
              <div className="patientsFeildsRow">
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Pulse (BPM)</label>
                      <input
                        type="text"
                        value={assessmentData.pulse}
                        onChange={(e) =>
                          handleInputChange("pulse", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">SPO2%</label>
                      <input
                        type="text"
                        value={assessmentData.spo2}
                        onChange={(e) =>
                          handleInputChange("spo2", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">SBP(mmHg)</label>
                      <input
                        type="text"
                        value={assessmentData.sbp}
                        onChange={(e) =>
                          handleInputChange("sbp", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">DBP(mmHg)</label>
                      <input
                        type="text"
                        value={assessmentData.dbp}
                        onChange={(e) =>
                          handleInputChange("dbp", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">MAP</label>
                      <input
                        type="text"
                        value={assessmentData.map}
                        onChange={(e) =>
                          handleInputChange("map", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Temp (S)</label>
                      <input
                        type="text"
                        value={assessmentData.temperature}
                        onChange={(e) =>
                          handleInputChange("temperature", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Res (bpm) </label>
                      <input
                        type="text"
                        value={assessmentData.respiration}
                        onChange={(e) =>
                          handleInputChange("respiration", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Pain Score</label>
                      <input
                        type="text"
                        value={assessmentData.painScore}
                        onChange={(e) =>
                          handleInputChange("painScore", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Height (CM)</label>
                      <input
                        type="text"
                        value={assessmentData.height}
                        onChange={(e) =>
                          handleInputChange("height", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Weight (KG)</label>
                      <input
                        type="text"
                        value={assessmentData.weight}
                        onChange={(e) =>
                          handleInputChange("weight", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">BSA</label>
                      <input
                        type="text"
                        value={assessmentData.bsa}
                        onChange={(e) =>
                          handleInputChange("bsa", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">BMI</label>
                      <input
                        type="text"
                        value={assessmentData.bmi}
                        onChange={(e) =>
                          handleInputChange("bmi", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Category</label>
                      <select
                        value={assessmentData.category}
                        onChange={(e) =>
                          handleInputChange("category", e.target.value)
                        }
                        required
                      >
                        <option value="">Select</option>
                        <option value="underweight">Underweight</option>
                        <option value="normal">Normal</option>
                        <option value="overweight">Overweight</option>
                        <option value="obese">Obese</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">BS</label>
                      <select
                        value={assessmentData.bs}
                        onChange={(e) =>
                          handleInputChange("bs", e.target.value)
                        }
                        required
                      >
                        <option value="">Select</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Creatinine </label>
                      <input
                        type="text"
                        value={assessmentData.creatinine}
                        onChange={(e) =>
                          handleInputChange("creatinine", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">eGFR</label>
                      <input
                        type="text"
                        value={assessmentData.egfr}
                        onChange={(e) =>
                          handleInputChange("egfr", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">eGFR</label>
                      <input
                        type="text"
                        value={assessmentData.egfr2}
                        onChange={(e) =>
                          handleInputChange("egfr2", e.target.value)
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="basicDetails">
              <div className="patientsFeildsRow">
                <div className="row">
                  <div className="col-xs-12 col-md-5">
                    <div className="feilds">
                      <label className="label">Symptoms / Complaints</label>
                      <textarea
                        rows="6"
                        value={assessmentData.symptoms}
                        required
                        onChange={(e) =>
                          handleInputChange("symptoms", e.target.value)
                        }
                      ></textarea>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Select Symptoms</label>
                      <select
                        value={assessmentData.selectedSymptoms}
                        onChange={(e) =>
                          handleInputChange("selectedSymptoms", e.target.value)
                        }
                        required
                      >
                        <option value="">Select</option>
                        <option value="fever">Fever</option>
                        <option value="cough">Cough</option>
                        <option value="headache">Headache</option>
                        <option value="fatigue">Fatigue</option>
                        <option value="nausea">Nausea</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="basicDetails">
              <h4>Medical History</h4>
              <div className="patientsFeildsRow">
                {medicalHisToryitems.map((medicalHisToryitemsItem, index) => (
                  <div className="row" key={medicalHisToryitemsItem.id}>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Diseases</label>
                        <select
                          value={
                            medicalHistoryData[medicalHisToryitemsItem.id]
                              ?.disease || ""
                          }
                          onChange={(e) =>
                            handleMedicalHistoryChange(
                              medicalHisToryitemsItem.id,
                              "disease",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="diabetes">Diabetes</option>
                          <option value="hypertension">Hypertension</option>
                          <option value="asthma">Asthma</option>
                          <option value="heart_disease">Heart Disease</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Duration </label>
                        <select
                          value={
                            medicalHistoryData[medicalHisToryitemsItem.id]
                              ?.duration || ""
                          }
                          onChange={(e) =>
                            handleMedicalHistoryChange(
                              medicalHisToryitemsItem.id,
                              "duration",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="1-6months">1-6 months</option>
                          <option value="6-12months">6-12 months</option>
                          <option value="1-2years">1-2 years</option>
                          <option value="2-5years">2-5 years</option>
                          <option value="5+years">5+ years</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Medications </label>
                        <select
                          value={
                            medicalHistoryData[medicalHisToryitemsItem.id]
                              ?.medication || ""
                          }
                          onChange={(e) =>
                            handleMedicalHistoryChange(
                              medicalHisToryitemsItem.id,
                              "medication",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="metformin">Metformin</option>
                          <option value="insulin">Insulin</option>
                          <option value="amlodipine">Amlodipine</option>
                          <option value="lisinopril">Lisinopril</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">&nbsp; </label>
                        <div
                          className="trashSection"
                          onClick={() =>
                            handleRemoveMedicalHistory(
                              medicalHisToryitemsItem.id,
                            )
                          }
                        >
                          <img src={Trash} alt="Delete" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="add-items">
                  <button
                    className="customeBtn btnLightBlueBorder"
                    onClick={handleMedicalHistory}
                  >
                    + Add Item
                  </button>
                </div>
              </div>
            </div>
            <div className="basicDetails">
              <h4>Surgical History</h4>
              <div className="patientsFeildsRow">
                {medicalSurgicalitems.map((medicalSurgicalitems, index) => (
                  <div className="row" key={medicalSurgicalitems.id}>
                    <div className="col-xs-12 col-md-6">
                      <div className="row">
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Surgical</label>
                            <select
                              value={
                                surgicalHistoryData[medicalSurgicalitems.id]
                                  ?.surgical || ""
                              }
                              onChange={(e) =>
                                handleSurgicalHistoryChange(
                                  medicalSurgicalitems.id,
                                  "surgical",
                                  e.target.value,
                                )
                              }
                              required
                            >
                              <option value="">Select</option>
                              <option value="appendectomy">Appendectomy</option>
                              <option value="cholecystectomy">
                                Cholecystectomy
                              </option>
                              <option value="hernia_repair">
                                Hernia Repair
                              </option>
                              <option value="cardiac_surgery">
                                Cardiac Surgery
                              </option>
                            </select>
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Surgeon Name </label>
                            <input
                              type="text"
                              value={
                                surgicalHistoryData[medicalSurgicalitems.id]
                                  ?.surgeonName || ""
                              }
                              onChange={(e) =>
                                handleSurgicalHistoryChange(
                                  medicalSurgicalitems.id,
                                  "surgeonName",
                                  e.target.value,
                                )
                              }
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Date </label>
                            <input
                              type="date"
                              value={
                                surgicalHistoryData[medicalSurgicalitems.id]
                                  ?.date || ""
                              }
                              max={getTodaysDate()}
                              onChange={(e) =>
                                handleSurgicalHistoryChange(
                                  medicalSurgicalitems.id,
                                  "date",
                                  e.target.value,
                                )
                              }
                              required
                            />
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Hospital </label>
                            <input
                              type="text"
                              value={
                                surgicalHistoryData[medicalSurgicalitems.id]
                                  ?.hospital || ""
                              }
                              onChange={(e) =>
                                handleSurgicalHistoryChange(
                                  medicalSurgicalitems.id,
                                  "hospital",
                                  e.target.value,
                                )
                              }
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">&nbsp; </label>
                        <div
                          className="trashSection"
                          onClick={() =>
                            handleRemoveSurgicalHistory(medicalSurgicalitems.id)
                          }
                        >
                          <img src={Trash} alt="Delete" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="add-items">
                  <button
                    className="customeBtn btnLightBlueBorder"
                    onClick={handleSurgicalHistory}
                  >
                    + Add Item
                  </button>
                </div>
              </div>
            </div>
            <div className="basicDetails">
              <h4>Additional Details</h4>
              <div className="patientsFeildsRow">
                {additionalDetails.map((additionalDetailsItem, index) => (
                  <div className="row" key={additionalDetailsItem.id}>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Type </label>
                        <select
                          value={
                            additionalDetailsData[additionalDetailsItem.id]
                              ?.type || ""
                          }
                          onChange={(e) =>
                            handleAdditionalDetailsChange(
                              additionalDetailsItem.id,
                              "type",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="smoking">Smoking</option>
                          <option value="alcohol">Alcohol</option>
                          <option value="exercise">Exercise</option>
                          <option value="diet">Diet</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Duration </label>
                        <select
                          value={
                            additionalDetailsData[additionalDetailsItem.id]
                              ?.duration || ""
                          }
                          onChange={(e) =>
                            handleAdditionalDetailsChange(
                              additionalDetailsItem.id,
                              "duration",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="rarely">Rarely</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Units </label>
                        <select
                          value={
                            additionalDetailsData[additionalDetailsItem.id]
                              ?.units || ""
                          }
                          onChange={(e) =>
                            handleAdditionalDetailsChange(
                              additionalDetailsItem.id,
                              "units",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="packets">Packets</option>
                          <option value="glasses">Glasses</option>
                          <option value="hours">Hours</option>
                          <option value="servings">Servings</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Frequency </label>
                        <select
                          value={
                            additionalDetailsData[additionalDetailsItem.id]
                              ?.frequency || ""
                          }
                          onChange={(e) =>
                            handleAdditionalDetailsChange(
                              additionalDetailsItem.id,
                              "frequency",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="1-2">1-2 times</option>
                          <option value="3-5">3-5 times</option>
                          <option value="5+">5+ times</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Action </label>
                        <select
                          value={
                            additionalDetailsData[additionalDetailsItem.id]
                              ?.action || ""
                          }
                          onChange={(e) =>
                            handleAdditionalDetailsChange(
                              additionalDetailsItem.id,
                              "action",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="continue">Continue</option>
                          <option value="reduce">Reduce</option>
                          <option value="stop">Stop</option>
                          <option value="monitor">Monitor</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-1">
                      <div className="feilds">
                        <label className="label">&nbsp; </label>
                        <div
                          className="trashSection"
                          onClick={() =>
                            handleRemoveAdditionalDetail(
                              additionalDetailsItem.id,
                            )
                          }
                        >
                          <img src={Trash} alt="Delete" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="add-items">
                  <button
                    className="customeBtn btnLightBlueBorder"
                    onClick={handleAdditionalDetails}
                  >
                    + Add Item
                  </button>
                </div>
              </div>
            </div>
            <div className="basicDetails">
              <h4>Immunization Record</h4>
              <div className="patientsFeildsRow">
                {/* Column headers */}
                <div className="row mb-1">
                  <div className="col-xs-12 col-md-3"><small className="text-muted">VACCINE</small></div>
                  <div className="col-xs-12 col-md-2"><small className="text-muted">DATE</small></div>
                  <div className="col-xs-12 col-md-3"><small className="text-muted">MANUFACTURER</small></div>
                  <div className="col-xs-12 col-md-2"><small className="text-muted">LOT NUMBER</small></div>
                  <div className="col-xs-12 col-md-2"><small className="text-muted">DOSE NUMBER</small></div>
                </div>

                {/* COVID-19 Vaccine (Dose 1) */}
                <div className="row mb-2">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <select disabled>
                        <option>COVID-19 Vaccine (Dose 1)</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="date"
                        value={immunizationData.covid19Dose1.date}
                        max={getTodaysDate()}
                        onChange={(e) =>
                          handleImmunizationChange("covid19Dose1", "date", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <input
                        type="text"
                        placeholder="e.g. Serum Institute of India"
                        value={immunizationData.covid19Dose1.manufacturer}
                        onChange={(e) =>
                          handleImmunizationChange("covid19Dose1", "manufacturer", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="text"
                        placeholder="e.g. BSCD12344SS"
                        value={immunizationData.covid19Dose1.lotNumber}
                        onChange={(e) =>
                          handleImmunizationChange("covid19Dose1", "lotNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={immunizationData.covid19Dose1.doseNumber}
                        onChange={(e) =>
                          handleImmunizationChange("covid19Dose1", "doseNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* COVID-19 Vaccine (Dose 2) */}
                <div className="row mb-2">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <select disabled>
                        <option>COVID-19 Vaccine (Dose 2)</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="date"
                        value={immunizationData.covid19Dose2.date}
                        max={getTodaysDate()}
                        onChange={(e) =>
                          handleImmunizationChange("covid19Dose2", "date", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <input
                        type="text"
                        placeholder="e.g. Serum Institute of India"
                        value={immunizationData.covid19Dose2.manufacturer}
                        onChange={(e) =>
                          handleImmunizationChange("covid19Dose2", "manufacturer", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="text"
                        placeholder="e.g. BSCD12344SS"
                        value={immunizationData.covid19Dose2.lotNumber}
                        onChange={(e) =>
                          handleImmunizationChange("covid19Dose2", "lotNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={immunizationData.covid19Dose2.doseNumber}
                        onChange={(e) =>
                          handleImmunizationChange("covid19Dose2", "doseNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Tetanus Booster */}
                <div className="row mb-2">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <select disabled>
                        <option>Tetanus Booster</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="date"
                        value={immunizationData.tetanusBooster.date}
                        max={getTodaysDate()}
                        onChange={(e) =>
                          handleImmunizationChange("tetanusBooster", "date", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <input
                        type="text"
                        placeholder="e.g. Serum Institute of India"
                        value={immunizationData.tetanusBooster.manufacturer}
                        onChange={(e) =>
                          handleImmunizationChange("tetanusBooster", "manufacturer", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="text"
                        placeholder="e.g. BSCD12344SS"
                        value={immunizationData.tetanusBooster.lotNumber}
                        onChange={(e) =>
                          handleImmunizationChange("tetanusBooster", "lotNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={immunizationData.tetanusBooster.doseNumber}
                        onChange={(e) =>
                          handleImmunizationChange("tetanusBooster", "doseNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Flu Vaccine */}
                <div className="row mb-2">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <select disabled>
                        <option>Flu Vaccine</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="date"
                        value={immunizationData.fluVaccine.date}
                        max={getTodaysDate()}
                        onChange={(e) =>
                          handleImmunizationChange("fluVaccine", "date", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <input
                        type="text"
                        placeholder="e.g. Serum Institute of India"
                        value={immunizationData.fluVaccine.manufacturer}
                        onChange={(e) =>
                          handleImmunizationChange("fluVaccine", "manufacturer", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="text"
                        placeholder="e.g. BSCD12344SS"
                        value={immunizationData.fluVaccine.lotNumber}
                        onChange={(e) =>
                          handleImmunizationChange("fluVaccine", "lotNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-2">
                    <div className="feilds">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={immunizationData.fluVaccine.doseNumber}
                        onChange={(e) =>
                          handleImmunizationChange("fluVaccine", "doseNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="basicDetails">
              <h4>Personal History</h4>
              <div className="patientsFeildsRow">
                {addPersonalHistory.map((addPersonalHistoryItems, index) => (
                  <div className="row" key={addPersonalHistoryItems.id}>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Diet </label>
                        <select
                          value={
                            personalHistoryData[addPersonalHistoryItems.id]
                              ?.diet || ""
                          }
                          onChange={(e) =>
                            handlePersonalHistoryChange(
                              addPersonalHistoryItems.id,
                              "diet",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="vegetarian">Vegetarian</option>
                          <option value="non-vegetarian">Non-Vegetarian</option>
                          <option value="vegan">Vegan</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Appetite </label>
                        <select
                          value={
                            personalHistoryData[addPersonalHistoryItems.id]
                              ?.appetite || ""
                          }
                          onChange={(e) =>
                            handlePersonalHistoryChange(
                              addPersonalHistoryItems.id,
                              "appetite",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="good">Good</option>
                          <option value="fair">Fair</option>
                          <option value="poor">Poor</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Sleep </label>
                        <select
                          value={
                            personalHistoryData[addPersonalHistoryItems.id]
                              ?.sleep || ""
                          }
                          onChange={(e) =>
                            handlePersonalHistoryChange(
                              addPersonalHistoryItems.id,
                              "sleep",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="good">Good</option>
                          <option value="fair">Fair</option>
                          <option value="poor">Poor</option>
                          <option value="insomnia">Insomnia</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Blader </label>
                        <select
                          value={
                            personalHistoryData[addPersonalHistoryItems.id]
                              ?.bladder || ""
                          }
                          onChange={(e) =>
                            handlePersonalHistoryChange(
                              addPersonalHistoryItems.id,
                              "bladder",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="normal">Normal</option>
                          <option value="frequent">Frequent</option>
                          <option value="painful">Painful</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-2">
                      <div className="feilds">
                        <label className="label">Bowel </label>
                        <select
                          value={
                            personalHistoryData[addPersonalHistoryItems.id]
                              ?.bowel || ""
                          }
                          onChange={(e) =>
                            handlePersonalHistoryChange(
                              addPersonalHistoryItems.id,
                              "bowel",
                              e.target.value,
                            )
                          }
                          required
                        >
                          <option value="">Select</option>
                          <option value="normal">Normal</option>
                          <option value="constipated">Constipated</option>
                          <option value="loose">Loose</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-1">
                      <div className="feilds">
                        <label className="label">&nbsp; </label>
                        <div
                          className="trashSection"
                          onClick={() =>
                            handleRemovehandlePersonalHistory(
                              addPersonalHistoryItems.id,
                            )
                          }
                        >
                          <img src={Trash} alt="Delete" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="add-items">
                  <button
                    className="customeBtn btnLightBlueBorder"
                    onClick={handlePersonalHistory}
                  >
                    + Add Item
                  </button>
                </div>
              </div>
            </div>

            {/* ── Physical Activity ─────────────────────────────────────── */}
            <div className="basicDetails">
              <h4>Physical Activity</h4>
              <div className="patientsFeildsRow">
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Steps Per Day</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="e.g. 8000"
                        value={physicalActivity.stepsPerDay}
                        onChange={(e) =>
                          setPhysicalActivity((prev) => ({
                            ...prev,
                            stepsPerDay: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Calories Burned</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="e.g. 350"
                        value={physicalActivity.caloriesBurned}
                        onChange={(e) =>
                          setPhysicalActivity((prev) => ({
                            ...prev,
                            caloriesBurned: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Sleep Duration (hrs)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        placeholder="e.g. 7.5"
                        value={physicalActivity.sleepDuration}
                        onChange={(e) =>
                          setPhysicalActivity((prev) => ({
                            ...prev,
                            sleepDuration: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Lifestyle ─────────────────────────────────────────────── */}
            <div className="basicDetails">
              <h4>Lifestyle</h4>
              <div className="patientsFeildsRow">
                <div className="row">
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Diet Type</label>
                      <select
                        value={lifestyle.dietType}
                        onChange={(e) =>
                          setLifestyle((prev) => ({
                            ...prev,
                            dietType: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select</option>
                        <option value="Vegan diet">Vegan diet</option>
                        <option value="Vegetarian diet">Vegetarian diet</option>
                        <option value="Mixed diet">Mixed diet</option>
                        <option value="Gluten-free diet">Gluten-free diet</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Smoking Behavior</label>
                      <select
                        value={lifestyle.smokingBehavior}
                        onChange={(e) =>
                          setLifestyle((prev) => ({
                            ...prev,
                            smokingBehavior: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select</option>
                        <option value="Non-smoker">Non-smoker</option>
                        <option value="Ex-smoker">Ex-smoker</option>
                        <option value="Smoker">Smoker</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-3">
                    <div className="feilds">
                      <label className="label">Alcohol Behavior</label>
                      <select
                        value={lifestyle.alcoholBehavior}
                        onChange={(e) =>
                          setLifestyle((prev) => ({
                            ...prev,
                            alcoholBehavior: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select</option>
                        <option value="Non-drinker">Non-drinker</option>
                        <option value="Occasional drinker">Occasional drinker</option>
                        <option value="Regular drinker">Regular drinker</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Women Health (female patients only) ───────────────────── */}
            {["female", "f", "male", "m"].includes(
              (patientData.gender || "").toLowerCase().trim(),
            ) && (
              <div className="basicDetails">
                <h4>Women Health</h4>
                <div className="patientsFeildsRow">
                  <div className="row">
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Age at Menarche (years)</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          placeholder="e.g. 13"
                          value={womenHealth.ageAtMenarche}
                          onChange={(e) =>
                            setWomenHealth((prev) => ({
                              ...prev,
                              ageAtMenarche: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="col-xs-12 col-md-3">
                      <div className="feilds">
                        <label className="label">Last Menstrual Period (LMP)</label>
                        <input
                          type="date"
                          max={getTodaysDate()}
                          value={womenHealth.lastMenstrualPeriod}
                          onChange={(e) =>
                            setWomenHealth((prev) => ({
                              ...prev,
                              lastMenstrualPeriod: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="basicDetails">
              <h4>Document Upload</h4>
              <div
                className="patientsFeildsRow"
                style={{ minHeight: "170px" }}
              >
                <div className="row">
                  <div className="col-xs-12 col-md-12">
                    <div className="uploads">
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        onChange={handleFileChange}
                      />
                      <h5>Upload file</h5>
                      <p>Drag & drop files here or click to upload</p>
                      <h6>(PDF, JPG, JPEG, PNG up to 10MB)</h6>
                    </div>
                    {existingDocuments.length > 0 && (
                      <div className="mb-3">
                        <h6 className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>
                          Uploaded Documents ({existingDocuments.length}):
                        </h6>
                        <div className="uploaded-docs-list d-flex flex-column gap-2">
                          {existingDocuments.map((doc, idx) => (
                            <div
                              key={idx}
                              className="d-flex align-items-center justify-content-between p-2 border rounded bg-light"
                              style={{ fontSize: "13px" }}
                            >
                              <div
                                className="d-flex align-items-center gap-2 text-truncate"
                                style={{ maxWidth: "55%" }}
                              >
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#2563EB"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  style={{ flexShrink: 0 }}
                                >
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                  <polyline points="14 2 14 8 20 8"></polyline>
                                  <line x1="16" y1="13" x2="8" y2="13"></line>
                                  <line x1="16" y1="17" x2="8" y2="17"></line>
                                  <polyline points="10 9 9 9 8 9"></polyline>
                                </svg>
                                <div className="text-truncate">
                                  <span
                                    className="fw-medium text-dark text-truncate d-block"
                                    title={getOriginalFileName(doc, `Document ${idx + 1}`)}
                                  >
                                    {getOriginalFileName(doc, `Document ${idx + 1}`)}
                                  </span>
                                  <div className="d-flex align-items-center gap-2">
                                    {doc.uploadDate && (
                                      <small className="text-muted" style={{ fontSize: "11px" }}>
                                        {formatUploadDate(doc.uploadDate)}
                                      </small>
                                    )}
                                    {doc.size && !isNaN(doc.size) && (
                                      <small className="text-muted" style={{ fontSize: "11px" }}>
                                        &bull; {formatFileSize(doc.size)}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                {confirmDeleteIndex === idx ? (
                                  <div className="d-flex align-items-center gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger py-0 px-2"
                                      style={{ fontSize: "11px", height: "26px" }}
                                      disabled={deletingDocIndex === idx}
                                      onClick={() => handleDeleteDocument(idx, getOriginalFileName(doc))}
                                    >
                                      {deletingDocIndex === idx ? "Deleting..." : "Confirm"}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary py-0 px-2"
                                      style={{ fontSize: "11px", height: "26px" }}
                                      onClick={() => setConfirmDeleteIndex(null)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary py-0 px-2"
                                      style={{ fontSize: "12px", height: "26px" }}
                                      disabled={downloadingDocIndex === idx}
                                      onClick={() => handleViewDocument(idx, getOriginalFileName(doc))}
                                      title="View in new tab"
                                    >
                                      View
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary py-0 px-2"
                                      style={{ fontSize: "12px", height: "26px" }}
                                      disabled={downloadingDocIndex === idx}
                                      onClick={() => handleDownloadDocument(idx, getOriginalFileName(doc))}
                                      title="Download file"
                                    >
                                      Download
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger py-0 px-2"
                                      style={{ fontSize: "12px", height: "26px" }}
                                      disabled={deletingDocIndex === idx}
                                      onClick={() => setConfirmDeleteIndex(idx)}
                                      title="Delete document"
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedFiles.length > 0 && (
                      <div className="mt-3 mb-3">
                        <h6 className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>
                          Files Staged for Upload ({selectedFiles.length}):
                        </h6>
                        <div className="selected-files-list">
                          {selectedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="d-flex justify-content-between align-items-center mb-1 p-2 border rounded bg-white"
                            >
                              <div
                                className="d-flex align-items-center gap-2 text-truncate"
                                style={{ maxWidth: "80%" }}
                              >
                                <span
                                  className="text-truncate fw-medium"
                                  title={file.name}
                                >
                                  {file.name}
                                </span>
                                {file.size > 0 && (
                                  <small className="text-muted flex-shrink-0" style={{ fontSize: "11px" }}>
                                    ({formatFileSize(file.size)})
                                  </small>
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-danger p-0"
                                onClick={() => removeFile(index)}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="agreeCheck">
              <div className="row">
                <div className="col-xs-12 col-md-12">
                  <div className="feilds">
                    <label className="checkBoxCustome" style={{ cursor: "pointer" }}>
                      <h4>
                        I consent to sharing this non-clinical health data with ABDM.
                      </h4>
                      <input
                        type="checkbox"
                        checked={Boolean(assessmentData.dataConsent)}
                        onChange={(e) =>
                          handleInputChange(
                            "dataConsent",
                            e.target.checked,
                          )
                        }
                      />
                      <span className="checkmark" />
                    </label>
                  </div>
                </div>
              </div>
            </div>
            </fieldset>
            <div className="basicDetailsAction">
              {!canWriteAssessment ? (
                <div className="d-flex align-items-center justify-content-between w-100">
                  <span className="badge bg-secondary px-3 py-2" style={{ fontSize: "13px" }}>
                    Read-Only Mode ({user?.role_name || "Viewing"}) — Write permission required to save
                  </span>
                  <button
                    type="button"
                    className="customeBtn btnEmpty"
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </button>
                </div>
              ) : (
                <>
                  {/* <button type="button" className="customeBtn btnEmpty me-2">Save Locally</button>
                  <button type="button" className="customeBtn btnEmpty me-2">Download PDF</button>
                  <button type="button" className="customeBtn btnLightBlue me-2">
                    Share to ABDM
                  </button> */}
                  <button
                    className="customeBtn btnLightBlue"
                    onClick={handleSave}
                    disabled={isSaving}
                    type="submit"
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                </>
              )}
            </div>
            {/* </form> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default Assessment;
